import {useContainer} from "#imports";
import {DependencyInjectId} from "@@/package/const/DependencyInjectId";
import {ContestListModel} from "~~/package/mapper/contest/model/ContestListModel";
import ContestMapper from "~~/package/mapper/contest/ContestMapper";
import {ContestLeaderBoardModel} from "~~/package/mapper/contest/model/ContestLeaderBoardModel";
// @ts-ignore
import type ContestResponse from "~~/package/apis/contest/response/ContestResponse";
import { PageModel } from "~~/package/mapper/common/model/PageModel";
import { ContestLeaderBoardExistsModel } from "~~/package/mapper/contest/model/ContestLeaderBoardExistsModel";
// @ts-ignore
import ContestRequest from "~~/package/apis/contest/response/ContestRequest";
import { CautionType } from "~~/package/types/CautionType";
import {useLearningStore} from "~/store/learning/learningStore";
import {useLearningService} from "~/composables/service/useLearningService";
import type {ContestModel} from "~~/package/mapper/contest/model/ContestModel";
import {TaskType} from "~~/package/mapper/learning/model/TaskType";
import {useFaroTracking} from "~/composables/useFaroTracking";

export const useContestService = () => {
    const contestMapper = useContainer<ContestMapper>(DependencyInjectId.ContestMapper);
    const learningStore = useLearningStore();
    const faroTracking = useFaroTracking();

    const getContestList = (Lang: string): Promise<ContestListModel[]> => {
        return contestMapper.getContestList(Lang);
    };

    const getContestDetail = (id:number, Lang: string): Promise<ContestListModel> => {
        return contestMapper.getContestDetail(id, Lang);
    };

    const getContestLeaderBoard = (id:number, page: number, itemSize: number): Promise<PageModel<ContestLeaderBoardModel>> => {
        return contestMapper.getContestLeaderBoardList(id, page, itemSize);
    };

    const getContestMyLeaderBoard = (id:number): Promise<ContestLeaderBoardModel> => {
        return contestMapper.getContestMyLeaderBoard(id);
    };

    const getContestAdminGrading = (id:number): Promise<ContestLeaderBoardExistsModel> => {
        return contestMapper.getContestAdminGrading(id);
    };

    const getContestAuthentication = (id:number, code: string): Promise<boolean> => {
        return contestMapper.getContestAuthentication(id, code);
    };

    const getContestReceive = (contestId: number, termsId: number): Promise<boolean> => {
        return contestMapper.getContestReceive(contestId, termsId);
    };

    const getContestData = (contestId: number, lang?: string): Promise<ContestResponse> => {
        return contestMapper.getContestData(contestId, lang);
    }

    const updateContestTaskWithLatestCode = async (contestTask: ContestModel, contestId: number): Promise<ContestModel> => {
        const latestCode = await useLearningService().getLatestSavedCode(contestTask.id, "contest", contestId);

        if([TaskType.MULTIPLE_CHOICE, TaskType.INSTRUCTION_MULTIPLE_CHOICE, TaskType.SHORT_QA].includes(contestTask.type as TaskType)) {
            return contestTask;
        } else {
            return {
                ...contestTask,
                submitCode: latestCode
            }
        }
    }

    const updateAllContestTaskCode = (contestTasks: ContestModel[], contestId: number): Promise<ContestModel[]> => {
        return Promise.all(contestTasks.map(contestTask => updateContestTaskWithLatestCode(contestTask, contestId)));
    }

    const getContestTasksWithLatestCodes = async (contestId: number, lang: string): Promise<ContestResponse> => {
        const contestData = await getContestData(contestId, lang);
        const contestTaskList = await updateAllContestTaskCode(contestData.contestTaskList, contestId);
        return {
            ...contestData,
            contestTaskList
        };
    }

    const setCurrentContestTask = (id: number, taskId: number): Promise<boolean> => {
        return contestMapper.setCurrentContestTask(id, taskId);
    }

    const submitAnswer = (contestRequest: ContestRequest): Promise<boolean> => {
        return contestMapper.submitAnswer(contestRequest, learningStore.tokenData)
            .then(result => {
                if (result) faroTracking.trackProblemSubmitted({
                    context: 'contest',
                    assessmentId: contestRequest?.contestId,
                    templateId: contestRequest?.taskTestTemplateId,
                    isSample: false,
                });
                return result;
            });
    }

    const setContestUserAction = (contestId: number, suspiciousAction: CautionType, actionContent?: string): Promise<number> => {
        return contestMapper.setContestUserAction(contestId, suspiciousAction, actionContent);
    }

    return {
        getContestList,
        getContestDetail,
        getContestLeaderBoard,
        getContestMyLeaderBoard,
        getContestAdminGrading,
        getContestAuthentication,
        getContestReceive,
        getContestData,
        getContestTasksWithLatestCodes,
        setCurrentContestTask,
        submitAnswer,
        setContestUserAction
    };
}
